import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useI18n } from '../context/I18nContext';
import { useCurrency } from '../context/CurrencyContext';
import { TrendingUp, DollarSign } from 'lucide-react';

const competitors = [
  { name: 'Adobe Acrobat Pro', monthly_eur: 23.99, monthly_usd: 22.99, color: '#ef4444', bg: 'bg-red-500/10', border: 'border-red-500/20', textColor: 'text-red-400' },
  { name: 'Foxit PDF Editor', monthly_eur: 17.99, monthly_usd: 14.99, color: '#f59e0b', bg: 'bg-amber-500/10', border: 'border-amber-500/20', textColor: 'text-amber-400' },
];

const hexopdf_prices = { eur: 4.99, usd: 4.99 };

export const PriceComparator = () => {
  const { lang } = useI18n();
  const { currency } = useCurrency();
  const [months, setMonths] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef(null);

  // Auto-animate when section comes into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          startAnimation();
        }
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [hasAnimated]);

  const startAnimation = () => {
    setIsAnimating(true);
    setMonths(1);
    let current = 1;
    const interval = setInterval(() => {
      current += 1;
      if (current > 60) {
        clearInterval(interval);
        setIsAnimating(false);
        return;
      }
      setMonths(current);
    }, 50); // 50ms per month = 3 seconds total
  };

  const formatPrice = (n) => {
    return new Intl.NumberFormat(lang === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
    }).format(n);
  };

  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  
  const getTimeLabel = () => {
    const labels = {
      en: { year: 'year', years: 'years', month: 'month', months: 'months', and: 'and' },
      fr: { year: 'an', years: 'ans', month: 'mois', months: 'mois', and: 'et' },
      es: { year: 'año', years: 'años', month: 'mes', months: 'meses', and: 'y' },
      de: { year: 'Jahr', years: 'Jahre', month: 'Monat', months: 'Monate', and: 'und' },
      it: { year: 'anno', years: 'anni', month: 'mese', months: 'mesi', and: 'e' },
      pt: { year: 'ano', years: 'anos', month: 'mês', months: 'meses', and: 'e' },
      nl: { year: 'jaar', years: 'jaar', month: 'maand', months: 'maanden', and: 'en' },
      pl: { year: 'rok', years: 'lata', month: 'miesiąc', months: 'miesięcy', and: 'i' },
      tr: { year: 'yıl', years: 'yıl', month: 'ay', months: 'ay', and: 've' },
      ru: { year: 'год', years: 'лет', month: 'месяц', months: 'месяцев', and: 'и' },
      ja: { year: '年', years: '年', month: 'ヶ月', months: 'ヶ月', and: '' },
      zh: { year: '年', years: '年', month: '个月', months: '个月', and: '' },
      ar: { year: 'سنة', years: 'سنوات', month: 'شهر', months: 'أشهر', and: 'و' },
      hi: { year: 'वर्ष', years: 'वर्ष', month: 'महीना', months: 'महीने', and: 'और' },
    };
    const l = labels[lang] || labels.en;
    const parts = [];
    if (years > 0) parts.push(`${years} ${years === 1 ? l.year : l.years}`);
    if (remainingMonths > 0) parts.push(`${remainingMonths} ${remainingMonths === 1 ? l.month : l.months}`);
    if (parts.length === 0) parts.push(`1 ${l.month}`);
    return parts.join(` ${l.and} `);
  };
  const timeLabel = getTimeLabel();

  const i18nLabels = {
    en: { tag: 'Price Comparator', title: 'How much do subscriptions really cost?', desc: 'Drag the slider and watch costs pile up.', onetime: 'One-time', save: 'You save', vs: 'vs Adobe', replay: 'Replay animation', mo: 'mo', month: 'month', year: 'year', years: 'years' },
    fr: { tag: 'Comparateur de prix', title: 'Combien co\u00fbtent vraiment les abonnements\u202f?', desc: 'Faites glisser le curseur et regardez les co\u00fbts s\u2019accumuler.', onetime: 'Paiement unique', save: 'Vous \u00e9conomisez', vs: 'vs Adobe', replay: 'Rejouer l\u2019animation', mo: 'mois', month: 'mois', year: 'an', years: 'ans' },
    es: { tag: 'Comparador de precios', title: '\u00bfCu\u00e1nto cuestan realmente las suscripciones?', desc: 'Mueve el cursor y mira c\u00f3mo se acumulan los costes.', onetime: 'Pago \u00fanico', save: 'Ahorras', vs: 'vs Adobe', replay: 'Repetir animaci\u00f3n', mo: 'mes', month: 'mes', year: 'a\u00f1o', years: 'a\u00f1os' },
    de: { tag: 'Preisvergleich', title: 'Was kosten Abonnements wirklich?', desc: 'Ziehen Sie den Regler und sehen Sie die Kosten wachsen.', onetime: 'Einmalig', save: 'Sie sparen', vs: 'vs Adobe', replay: 'Animation wiederholen', mo: 'Mo.', month: 'Monat', year: 'Jahr', years: 'Jahre' },
    it: { tag: 'Comparatore prezzi', title: 'Quanto costano davvero gli abbonamenti?', desc: 'Trascina il cursore e guarda i costi accumularsi.', onetime: 'Una tantum', save: 'Risparmi', vs: 'vs Adobe', replay: 'Ripeti animazione', mo: 'mese', month: 'mese', year: 'anno', years: 'anni' },
    pt: { tag: 'Comparador de pre\u00e7os', title: 'Quanto custam realmente as assinaturas?', desc: 'Arraste o cursor e veja os custos se acumularem.', onetime: 'Pagamento \u00fanico', save: 'Voc\u00ea economiza', vs: 'vs Adobe', replay: 'Repetir anima\u00e7\u00e3o', mo: 'm\u00eas', month: 'm\u00eas', year: 'ano', years: 'anos' },
    nl: { tag: 'Prijsvergelijker', title: 'Wat kosten abonnementen echt?', desc: 'Versleep de schuifbalk en zie de kosten oplopen.', onetime: 'Eenmalig', save: 'U bespaart', vs: 'vs Adobe', replay: 'Animatie herhalen', mo: 'mnd', month: 'maand', year: 'jaar', years: 'jaar' },
    pl: { tag: 'Por\u00f3wnanie cen', title: 'Ile naprawd\u0119 kosztuj\u0105 subskrypcje?', desc: 'Przesu\u0144 suwak i zobacz narastaj\u0105ce koszty.', onetime: 'Jednorazowo', save: 'Oszcz\u0119dzasz', vs: 'vs Adobe', replay: 'Powt\u00f3rz animacj\u0119', mo: 'mies.', month: 'miesi\u0105c', year: 'rok', years: 'lata' },
    tr: { tag: 'Fiyat Karşılaştırma', title: 'Abonelikler gerçekte ne kadara mal oluyor?', desc: 'Kaydırıcıyı sürükleyin ve maliyetlerin artışını görün.', onetime: 'Tek seferlik', save: 'Tasarrufunuz', vs: 'vs Adobe', replay: 'Animasyonu tekrarla', mo: 'ay', month: 'ay', year: 'yıl', years: 'yıl' },
    ru: { tag: 'Сравнение цен', title: 'Сколько на самом деле стоят подписки?', desc: 'Перетащите ползунок и увидите, как растут расходы.', onetime: 'Разовый платёж', save: 'Экономия', vs: 'vs Adobe', replay: 'Повторить анимацию', mo: 'мес', month: 'месяц', year: 'год', years: 'лет' },
    ja: { tag: '価格比較', title: 'サブスクリプションの本当のコストは？', desc: 'スライダーを動かして、コストの積み上がりを見てください。', onetime: '一括払い', save: '節約額', vs: 'vs Adobe', replay: 'アニメーション再生', mo: '月', month: 'ヶ月', year: '年', years: '年' },
    zh: { tag: '价格对比', title: '订阅真正花费多少？', desc: '拖动滑块，查看费用的累积。', onetime: '一次性', save: '节省', vs: 'vs Adobe', replay: '重播动画', mo: '月', month: '个月', year: '年', years: '年' },
    ar: { tag: 'مقارنة الأسعار', title: 'كم تكلف الاشتراكات فعلاً؟', desc: 'اسحب المؤشر وشاهد التكاليف تتراكم.', onetime: 'دفعة واحدة', save: 'توفيرك', vs: 'vs Adobe', replay: 'إعادة الرسوم المتحركة', mo: 'شهر', month: 'شهر', year: 'سنة', years: 'سنوات' },
    hi: { tag: 'मूल्य तुलना', title: 'सदस्यता वास्तव में कितनी महंगी है?', desc: 'स्लाइडर खींचें और लागत बढ़ते देखें।', onetime: 'एकमुश्त', save: 'आपकी बचत', vs: 'vs Adobe', replay: 'एनिमेशन दोबारा चलाएं', mo: 'माह', month: 'महीना', year: 'वर्ष', years: 'वर्ष' },
  };
  const labels = i18nLabels[lang] || i18nLabels.en;

  const hexoPrice = hexopdf_prices[currency] || hexopdf_prices.eur;
  const getMonthly = (comp) => currency === 'usd' ? comp.monthly_usd : comp.monthly_eur;
  const maxCost = getMonthly(competitors[0]) * 60;

  return (
    <section ref={sectionRef} className="relative py-24 md:py-32 bg-background" data-testid="price-comparator-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-red-500/70 font-medium" data-testid="comparator-tag">
            {labels.tag}
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-foreground mt-3" data-testid="comparator-title">
            {labels.title}
          </h2>
          <p className="text-muted-foreground text-lg mt-4 max-w-2xl">
            {labels.desc}
          </p>
        </motion.div>

        {/* Time display */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-card border border-border">
            <TrendingUp className="w-5 h-5 text-blue-500" />
            <span className="font-heading text-2xl font-bold text-foreground" data-testid="comparator-time">{timeLabel}</span>
          </div>
        </div>

        {/* Slider */}
        <div className="max-w-3xl mx-auto mb-12">
          <input
            type="range"
            min="1"
            max="60"
            value={months}
            onChange={(e) => { setMonths(parseInt(e.target.value)); setIsAnimating(false); }}
            className="w-full h-2 bg-secondary rounded-full appearance-none cursor-pointer accent-blue-500 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500 [&::-webkit-slider-thumb]:shadow-[0_0_10px_rgba(59,130,246,0.5)] [&::-webkit-slider-thumb]:cursor-grab"
            data-testid="comparator-slider"
          />
          <div className="flex justify-between text-xs text-muted-foreground/60 mt-2">
            <span>1 {labels.month}</span>
            <span>1 {labels.year}</span>
            <span>2 {labels.years}</span>
            <span>3 {labels.years}</span>
            <span>5 {labels.years}</span>
          </div>
        </div>

        {/* Cost comparison cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {competitors.map((comp, i) => {
            const monthly = getMonthly(comp);
            const totalCost = monthly * months;
            const barWidth = Math.min((totalCost / maxCost) * 100, 100);
            return (
              <div key={i} className={`p-6 rounded-2xl ${comp.bg} border ${comp.border}`} data-testid={`comparator-card-${i}`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`font-semibold text-sm ${comp.textColor}`}>{comp.name}</h3>
                  <span className="text-muted-foreground text-xs">{formatPrice(monthly)}/{labels.mo}</span>
                </div>
                <div className="font-heading text-3xl font-bold text-foreground mb-3" data-testid={`comparator-cost-${i}`}>
                  {formatPrice(totalCost)}
                </div>
                <div className="h-2 bg-secondary rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-100" style={{ width: `${barWidth}%`, backgroundColor: comp.color }} />
                </div>
              </div>
            );
          })}

          {/* HexoPDF card */}
          <div className="p-6 rounded-2xl bg-blue-500/10 border border-blue-500/30 relative overflow-hidden" data-testid="comparator-card-hexopdf">
            <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/10 blur-[40px]" />
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-sm text-blue-600 dark:text-blue-400">HexoPDF</h3>
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-600 dark:text-blue-300">
                  {labels.onetime}
                </span>
              </div>
              <div className="font-heading text-3xl font-bold text-foreground mb-3" data-testid="comparator-cost-hexopdf">
                {formatPrice(hexoPrice)}
              </div>
              <div className="h-2 bg-secondary rounded-full overflow-hidden">
                <div className="h-full rounded-full bg-blue-500" style={{ width: `${Math.max((hexoPrice / maxCost) * 100, 1)}%` }} />
              </div>
              {months > 1 && (
                <div className="mt-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                  <p className="text-emerald-600 dark:text-emerald-400 text-sm font-medium" data-testid="comparator-savings">
                    {labels.save}{' '}
                    <span className="font-bold">{formatPrice(getMonthly(competitors[0]) * months - hexoPrice)}</span>
                    {' '}{labels.vs}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Replay button */}
        <div className="text-center mt-8">
          <button onClick={startAnimation} disabled={isAnimating}
            className="text-sm text-muted-foreground hover:text-blue-500 transition-colors disabled:opacity-50" data-testid="comparator-replay-btn">
            {labels.replay}
          </button>
        </div>
      </div>
    </section>
  );
};
